/**
 * Qual motor converte o vídeo: WebCodecs (hardware) ou ffmpeg.wasm.
 *
 * Mesmo papel do `audioPath.ts` no recurso de gravação — e, como lá, dá para
 * forçar um caminho em runtime para depurar, sem rebuild:
 *
 *   localStorage['whiz.videoPathOverride'] = 'webcodecs' | 'wasm' | 'auto'
 *
 * O caminho WebCodecs é uma ACELERAÇÃO, não um requisito: ffmpeg.wasm converte
 * em qualquer navegador, só que single-thread e lento. Por isso a regra abaixo é
 * conservadora — na dúvida, `wasm`, que funciona em todo lugar.
 */

import { codecH264Minimo } from './nivelH264';

export type CaminhoDeVideo = 'webcodecs' | 'wasm';

/** Containers que o demuxer do caminho WebCodecs (mp4box.js) sabe ler. */
const CONTAINERS_ISOBMFF = [
    'video/mp4',
    'video/quicktime',
    'video/x-m4v',
    'video/3gpp',
];

/**
 * O nível H.264 é CALCULADO a partir das dimensões (ver `nivelH264.ts`), não
 * escolhido por tentativa contra o `isConfigSupported`.
 *
 * O motivo é medido: no Firefox o `isConfigSupported` responde `supported:
 * true` para qualquer nível pedido, sem olhar a resolução, e o encoder estampa
 * no SPS exatamente o que foi pedido. Pedindo L4.2 num 1918x904 saía
 * `avc1.42c02a` e a Meta recusava com 131053 "Media file scrutiny ...
 * mediaEngineStatus: 0"; pedindo L3.1 saía `avc1.42c01f`, que é stream NÃO
 * CONFORME (L3.1 comporta 3600 macroblocos, o quadro tem 6840). O Chrome,
 * diferente, ignora o excesso e emite o nível correto — e por isso só o
 * Firefox falhava.
 *
 * Aqui o `isConfigSupported` é porteiro de CAPACIDADE, nunca selecionador.
 */

function overrideManual(): CaminhoDeVideo | null {
    try {
        const v = localStorage.getItem('whiz.videoPathOverride');
        if (v === 'webcodecs' || v === 'wasm') return v;
    } catch { /* storage bloqueado */ }
    return null;
}

export function containerEhIsobmff(mime?: string | null): boolean {
    const base = (mime ?? '').split(';').at(0)?.trim().toLowerCase() ?? '';
    return CONTAINERS_ISOBMFF.includes(base);
}

export interface DecisaoDeVideo {
    caminho: CaminhoDeVideo;
    /** Codec aceito pelo encoder, quando `caminho === 'webcodecs'`. */
    codec?: string;
}

/**
 * Decide o caminho para ESTE arquivo, e com qual codec.
 *
 * Assíncrono porque `isConfigSupported` é a única resposta confiável sobre o
 * encoder: a presença de `VideoEncoder` no `window` não garante que o perfil e o
 * NÍVEL pedidos existam.
 *
 * As dimensões devem ser as de **saída** (depois de qualquer redução do plano de
 * compressão): é o quadro que vai ser codificado que o nível precisa suportar.
 * Consultar com as de entrada faria um 4K que será reduzido a 640 cair no wasm
 * sem necessidade.
 */
export async function resolverCaminhoDeVideo(
    mime: string | null | undefined,
    larguraPx?: number | null,
    alturaPx?: number | null,
): Promise<DecisaoDeVideo> {
    const forcado = overrideManual();

    // Sem WebCodecs (Firefox, hoje) → wasm. Vale mesmo com override: não há como
    // forçar uma API que não existe.
    if (typeof VideoEncoder === 'undefined' || typeof VideoDecoder === 'undefined') {
        return { caminho: 'wasm' };
    }
    if (forcado === 'wasm') return { caminho: 'wasm' };

    // mp4box.js só lê ISOBMFF. WebM/MKV/AVI vão para o ffmpeg, que demuxa tudo.
    if (forcado !== 'webcodecs' && !containerEhIsobmff(mime)) return { caminho: 'wasm' };

    const width = larguraPx && larguraPx > 0 ? larguraPx : 1280;
    const height = alturaPx && alturaPx > 0 ? alturaPx : 720;

    const codec = codecH264Minimo(width, height);
    // Quadro que nem o maior nível da tabela comporta: o ffmpeg.wasm não tem
    // esse limite.
    if (!codec) return { caminho: 'wasm' };

    try {
        const suporte = await VideoEncoder.isConfigSupported({
            codec,
            width,
            height,
            bitrate: 1_000_000,
        });
        return suporte.supported ? { caminho: 'webcodecs', codec } : { caminho: 'wasm' };
    } catch {
        // `isConfigSupported` lança com configuração inválida em vez de
        // devolver `supported: false`.
        return { caminho: 'wasm' };
    }
}
